import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Helper: Parse & build readable device info
function buildDeviceInfo(model, osVersion, userAgent) {
  let device = model || "Unknown";
  let os = osVersion || "Unknown";

  // Try to clean iOS
  if (/iPhone/.test(userAgent)) {
    device = "iPhone";
    const osMatch = userAgent.match(/OS (\d+[_\d]*)/);
    if (osMatch) os = "iOS " + osMatch[1].replace(/_/g, ".");
  }

  // Try to clean Android
  if (/Android/.test(userAgent)) {
    device = "Android";
    const osMatch = userAgent.match(/Android\s([0-9.]+)/);
    if (osMatch) os = "Android " + osMatch[1];
  }

  // Windows
  if (/Windows/.test(userAgent)) {
    device = "Windows PC";
    const osMatch = userAgent.match(/Windows NT\s([0-9.]+)/);
    if (osMatch) os = "Windows " + osMatch[1];
  }

  // macOS
  if (/Macintosh/.test(userAgent)) {
    device = "Mac";
    os = "macOS";
  }

  return `${device}, ${os}`;
}

// Async thunk to get device info + IP address
export const fetchDeviceInfo = createAsyncThunk(
  "device/fetchDeviceInfo",
  async (_, ThunkAPI) => {
    try {
      if (typeof window === "undefined") return {};

      const userAgent = navigator.userAgent || "";
      const brand = navigator.vendor || "Unknown";
      const model = navigator.platform || "Unknown";
      const osVersion = navigator.appVersion || "Unknown";

      // Extract browser name & version
      let browserName = "Unknown";
      let browserVersion = "Unknown";

      if (/chrome|crios|crmo/i.test(userAgent)) {
        browserName = "Chrome";
        browserVersion =
          userAgent.match(/Chrom(e|ium)\/([0-9]+)\./)?.[2] || "Unknown";
      } else if (/firefox|fxios/i.test(userAgent)) {
        browserName = "Firefox";
        browserVersion =
          userAgent.match(/Firefox\/([0-9]+)\./)?.[1] || "Unknown";
      } else if (
        /safari/i.test(userAgent) &&
        !/chrome|crios|crmo/i.test(userAgent)
      ) {
        browserName = "Safari";
        browserVersion =
          userAgent.match(/Version\/([0-9]+)\./)?.[1] || "Unknown";
      } else if (/edg/i.test(userAgent)) {
        browserName = "Edge";
        browserVersion =
          userAgent.match(/Edg\/([0-9]+)\./)?.[1] || "Unknown";
      }

      // Get public IP
      let ipAddress = "";
      try {
        const res = await fetch("https://api.ipify.org?format=json");
        const data = await res.json();
        ipAddress = data.ip;
      } catch (e) {
        console.error("Failed to get IP:", e);
      }

      // Build combined device info
      const deviceInfo = buildDeviceInfo(model, osVersion, userAgent);

      return {
        userAgent,
        brand,
        model,
        osVersion,
        ipAddress,
        browserName,
        browserVersion,
        deviceInfo, // 👈 combined string like "iPhone, iOS 16"
      };
    } catch (error) {
      return ThunkAPI.rejectWithValue("Failed to fetch device info");
    }
  }
);

// Initial state
const initialState = {
  userAgent: "",
  brand: "",
  model: "",
  osVersion: "",
  ipAddress: "",
  browserName: "",
  browserVersion: "",
  deviceInfo: "", // 👈 added here
};

const deviceSlice = createSlice({
  name: "device",
  initialState,
  reducers: {
    setDeviceInfo: (state, action) => {
      const {
        userAgent,
        brand,
        model,
        osVersion,
        ipAddress,
        browserName,
        browserVersion,
        deviceInfo,
      } = action.payload;

      state.userAgent = userAgent;
      state.brand = brand;
      state.model = model;
      state.osVersion = osVersion;
      state.ipAddress = ipAddress;
      state.browserName = browserName;
      state.browserVersion = browserVersion;
      state.deviceInfo = deviceInfo;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeviceInfo.fulfilled, (state, action) => {
        const {
          userAgent,
          brand,
          model,
          osVersion,
          ipAddress,
          browserName,
          browserVersion,
          deviceInfo,
        } = action.payload;

        state.userAgent = userAgent;
        state.brand = brand;
        state.model = model;
        state.osVersion = osVersion;
        state.ipAddress = ipAddress;
        state.browserName = browserName;
        state.browserVersion = browserVersion;
        state.deviceInfo = deviceInfo;
      })
      .addCase(fetchDeviceInfo.rejected, (state, action) => {
        console.error("fetchDeviceInfo failed:", action.payload);
      });
  },
});

export const { setDeviceInfo } = deviceSlice.actions;
export default deviceSlice.reducer;
